<template>
  <!--
    Nota presa como "CT-e em geração" (doc_fiscal.criando_cte = 1).
    Ver TMS-6446 (bug) e TMS-8776 (este modal). Contrato no ADR-0014 do isend-backend.
  -->
  <b-modal
    ref="notaTravada"
    :title="titulo"
    size="lg"
    no-close-on-backdrop
    v-model="showModal"
  >
    <div class="loadingDataPosition">
      <div class="loadingData" v-if="loading">
        <div class="loadingDataSpinner">
          <spinner
            text-fg-color="#ffffff"
            message="Carregando..."
          ></spinner>
        </div>
      </div>

      <!-- Com documento ativo (CT-e ou RPS), o alerta abaixo reproduz o corpo do backend com a cor da situação -->
      <p v-if="!invoice.cte">{{ mensagem }}</p>

      <b-alert
        v-if="invoice.cte"
        show
        :variant="situacaoCte.variant"
      >
        Esta nota já possui o {{ situacaoCte.documento }} Nº
        {{ invoice.cte.num_fiscal }}/{{ invoice.cte.serie }}
        {{ situacaoCte.rotulo }}.
        <span v-if="situacaoCte.orientacao">
          {{ situacaoCte.orientacao }}
        </span>
        <!-- Mesmos textos de InvoiceGenerationLockedException::body() no isend-backend -->
        <span v-if="invoice.released">
          A marca "em geração" foi liberada e nada foi reemitido. Se esta nota
          precisa mesmo de um novo documento (por exemplo, em outra carga),
          confira com a operação e gere novamente.
        </span>
        <span v-else>
          Confira com a operação antes de gerar um novo documento para ela.
        </span>
      </b-alert>

      <!-- `stale` ausente: o backend não conseguiu consultar a nota (o corpo orienta o chamado) -->
      <b-alert v-if="invoice.stale === false" show variant="warning">
        A geração desta nota começou há poucos minutos e ainda pode estar em
        andamento. Aguarde alguns minutos antes de liberar.
      </b-alert>

      <b-alert v-if="retorno" show :variant="retorno.variant">
        {{ retorno.texto }}
      </b-alert>

      <b-btn
        variant="link"
        class="p-0"
        @click="mostraDetalhes = !mostraDetalhes"
      >
        <i
          :class="mostraDetalhes ? 'fa fa-chevron-down' : 'fa fa-chevron-right'"
        ></i>
        Detalhes técnicos
      </b-btn>
      <b-collapse id="nota_travada_detalhes" v-model="mostraDetalhes">
        <table class="table table-sm mt-2 mb-0">
          <tbody>
            <tr>
              <th>docfis_id</th>
              <td>{{ invoice.docfis_id }}</td>
            </tr>
            <tr>
              <th>unidade_id</th>
              <td>{{ invoice.unidade_id }}</td>
            </tr>
            <tr>
              <th>criando_cte_em</th>
              <td>{{ invoice.criando_cte_em }}</td>
            </tr>
            <tr>
              <th>criando_cte_por</th>
              <td>{{ invoice.criando_cte_por || "não registrado" }}</td>
            </tr>
            <tr>
              <th>trace_id</th>
              <td>{{ traceId || "não informado" }}</td>
            </tr>
          </tbody>
        </table>
      </b-collapse>
    </div>

    <div slot="modal-footer" class="w-100">
      <div class="float-right">
        <b-btn @click="showModal = false" variant="secondary"> Fechar </b-btn>
        <b-btn
          @click="onCopiarDados"
          variant="secondary"
          :disabled="loading"
        >
          Copiar dados para chamado
        </b-btn>
        <b-btn
          v-if="podeLiberar"
          @click="onLiberarTrava"
          variant="primary"
          :disabled="loading"
        >
          Liberar e gerar novamente
        </b-btn>
      </div>
    </div>
  </b-modal>
</template>
<script>
import moment from "moment";
import { copiarParaAreaDeTransferencia } from "../utils/copiarParaAreaDeTransferencia";

// StatusSefazEnum no isend-backend
const STATUS_SEFAZ_AUTORIZADO = 4;
const STATUS_SEFAZ_REJEITADO = 5;
const STATUS_SEFAZ_ERRO = 9;
// `locked_invoice.cte.tipo` no isend-backend (InvoiceGenerationLock::findActiveDocument)
const TIPO_DOCUMENTO_RPS = "rps";
const ORIENTACAO_CORRIGIR_CTE =
  "Corrija e reenvie este CT-e: gerar um novo documento agora emitiria um segundo CT-e para a mesma nota.";

export default {
  data() {
    return {
      showModal: false,
      loading: false,
      mostraDetalhes: false,
      titulo: "",
      mensagem: "",
      traceId: null,
      invoice: {},
      // Mesmos dados do POST /api/gerardoc/store que travou, para reenviar
      dadosGeracao: null,
      // Já liberou nesta abertura: não oferece liberar de novo
      liberado: false,
      retorno: null,
    };
  },
  computed: {
    // `cte` é o documento ativo da nota (ADR-0014 D5): CT-e que não está
    // Cancelado, Inutilizado ou Denegado, ou RPS não cancelado (frete
    // intramunicipal), distinguidos por `tipo`. Sem `tipo`, é o contrato
    // anterior do backend, que só enviava CT-e; sem status_sefaz, só autorizado.
    situacaoCte() {
      var cte = this.invoice.cte;
      if (!cte) {
        return null;
      }
      // RPS não passa pela SEFAZ: mesmo texto de InvoiceGenerationLockedException::body()
      if (cte.tipo === TIPO_DOCUMENTO_RPS) {
        return {
          documento: "RPS",
          rotulo: "ativo",
          variant: "info",
          orientacao: "",
        };
      }
      var status = cte.status_sefaz == null ? null : Number(cte.status_sefaz);
      if (status === null || status === STATUS_SEFAZ_AUTORIZADO) {
        return {
          documento: "CT-e",
          rotulo: "autorizado",
          variant: "info",
          orientacao: "",
        };
      }
      // Rejeitado e Erro não terão retorno da SEFAZ: o CT-e é corrigido e reenviado
      if (status === STATUS_SEFAZ_REJEITADO) {
        return {
          documento: "CT-e",
          rotulo: "rejeitado pela SEFAZ, aguardando correção",
          variant: "warning",
          orientacao: ORIENTACAO_CORRIGIR_CTE,
        };
      }
      if (status === STATUS_SEFAZ_ERRO) {
        return {
          documento: "CT-e",
          rotulo: "com erro de emissão, aguardando correção",
          variant: "warning",
          orientacao: ORIENTACAO_CORRIGIR_CTE,
        };
      }
      return {
        documento: "CT-e",
        rotulo: "em processamento na SEFAZ",
        variant: "warning",
        orientacao:
          "Aguarde o retorno da SEFAZ: gerar novamente agora pode emitir um segundo CT-e para a mesma nota.",
      };
    },
    // Qualquer documento ativo bloqueia: reenviar com um em processamento emite outro
    podeLiberar() {
      return (
        !this.liberado &&
        this.invoice.can_release === true &&
        !this.invoice.cte &&
        this.invoice.stale === true &&
        this.getPermissionFw("gerardoc.store")
      );
    },
  },
  methods: {
    /**
     * Abre o modal a partir do envelope do store com code GERARDOC_INVOICE_LOCKED.
     * `dadosGeracao` é o form.data() enviado no store, reaproveitado no reenvio.
     * Só ocorre com 1 nota. Com 2+ o backend enfileira GeraDocLote → GerarDoc, e
     * o lote TAMBÉM passa pelo lock: o claim acontece dentro do job GerarDoc.
     * Lá, uma nota travada faz o grupo inteiro daquele GerarDoc ficar sem CT-e
     * nessa rodada, e a mensagem vai para a aba Erros, não para este modal
     * (ADR-0014 D4).
     */
    abrir(response, dadosGeracao) {
      this.titulo = response.title || "Nota marcada como em geração";
      this.mensagem = response.message || "";
      this.traceId = response.trace_id || null;
      this.invoice = Object.assign({}, response.locked_invoice);
      this.dadosGeracao = JSON.parse(JSON.stringify(dadosGeracao || {}));
      this.liberado = false;
      this.retorno = null;
      this.mostraDetalhes = false;
      this.loading = false;
      this.showModal = true;
    },
    formataDataHora(value, fmt = "DD/MM/YYYY HH:mm") {
      if (value == null || value === "") {
        return "";
      }
      // Aceita ISO8601 (locked_invoice) e "YYYY-MM-DD HH:mm:ss" (grid)
      var data = moment(String(value).replace(" ", "T"));
      return data.isValid() ? data.format(fmt) : String(value);
    },
    textoChamado() {
      var inv = this.invoice;
      return [
        "Nota " + inv.num_fiscal + " série " + inv.serie,
        "docfis_id: " + inv.docfis_id,
        "unidade_id: " + inv.unidade_id,
        "Em geração desde: " +
          (this.formataDataHora(inv.criando_cte_em) || "não registrado"),
        "Requisição que travou: " + (inv.criando_cte_por || "não registrado"),
        "Correlação: " + (this.traceId || "não informado"),
      ].join("\n");
    },
    onCopiarDados() {
      var vm = this;
      copiarParaAreaDeTransferencia(vm.textoChamado()).then((copiou) => {
        if (copiou) {
          vm.$snotify.success(
            "Dados copiados. Cole no chamado.",
            "Copiado"
          );
        } else {
          vm.$snotify.warning(
            "Não foi possível copiar. Abra os detalhes técnicos e copie manualmente.",
            "Atenção"
          );
        }
      });
    },
    mensagemDeErro(errors, padrao) {
      if (errors && typeof errors.message === "string" && errors.message) {
        return errors.message;
      }
      return padrao;
    },
    onLiberarTrava() {
      var vm = this;
      vm.loading = true;
      vm.retorno = null;
      new Form({ docfis_id: vm.invoice.docfis_id })
        .post("/api/gerardoc/liberar-trava")
        .then((response) => {
          if (response.locked_invoice) {
            vm.invoice = Object.assign({}, vm.invoice, response.locked_invoice);
          }
          if (response.released) {
            vm.liberado = true;
          }
          if (response.released && response.resend) {
            vm.retorno = {
              variant: "success",
              texto: response.message || "Marca liberada. Gerando novamente...",
            };
            vm.onGerarNovamente();
            return;
          }
          vm.loading = false;
          vm.retorno = {
            variant: response.released ? "success" : "warning",
            texto: response.message || "",
          };
        })
        .catch((errors) => {
          // 409 e 404 chegam aqui como error.response.data, todos com `message`.
          // Os 409 do liberar-trava são três (ADR-0014, contrato):
          // - lock recente: `locked_invoice` com `criando_cte_em`;
          // - a marca mudou de estado entre a leitura e a liberação (outra
          //   liberação concorrente): `locked_invoice` sem `criando_cte_em`;
          // - erro de validação: envelope padrão, sem `released` (o handler
          //   global do backend converte ValidationException em 409, não 422).
          vm.loading = false;
          var texto = vm.mensagemDeErro(
            errors,
            "Não foi possível liberar a nota. Tente novamente ou abra um chamado."
          );
          var lockedInvoice = errors && errors.locked_invoice;
          if (lockedInvoice) {
            vm.invoice = Object.assign({}, vm.invoice, lockedInvoice);
          }
          if (lockedInvoice && !lockedInvoice.criando_cte_em) {
            // Tentar de novo daqui não muda nada: o backend pede para atualizar a
            // tela. Esconde o botão e recarrega a grade com o estado real.
            vm.liberado = true;
            vm.retorno = { variant: "info", texto: texto };
            vm.$emit("atualizar");
            return;
          }
          vm.retorno = { variant: "warning", texto: texto };
          vm.$snotify.warning(texto, "Atenção");
        });
    },
    onGerarNovamente() {
      var vm = this;
      vm.loading = true;
      new Form(vm.dadosGeracao)
        .post("/api/gerardoc/store")
        .then((response) => {
          if (
            response &&
            response.code === "GERARDOC_INVOICE_LOCKED" &&
            response.locked_invoice
          ) {
            // Travou de novo (outra nota ou corrida): mostra o novo estado
            vm.abrir(response, vm.dadosGeracao);
            vm.$emit("gerado");
            return;
          }
          vm.showModal = false;
          new ReturnApi(response, vm, true, false, false, false);
          vm.$emit("gerado");
        })
        .catch((errors) => {
          // Mostra só no modal: ReturnApi abriria um alerta por cima dele
          vm.loading = false;
          vm.retorno = {
            variant: "danger",
            texto: vm.mensagemDeErro(
              errors,
              "A nota foi liberada, mas a nova geração falhou. Tente gerar novamente pela tela."
            ),
          };
        });
    },
  },
};
</script>
